import React, { useMemo } from 'react';
import tinycolor from 'tinycolor2';

/**
 * CustomGraphLegend - Reusable custom legend component for Chart.js graphs
 * 
 * @param {Array} datasets - Chart.js datasets array to generate legend from
 * @param {Function} sortFunction - Optional sorting function for legend items (a, b, getLabelFn)
 * @param {String} customColorForArrays - optional color to use for datasets with array background colors (e.g. bar charts with different colors per bar)
 */
export default function CustomGraphLegend({ 
	datasets = [],
	sortFunction = null,
	customColorForArrays = null
}) {

	const backgroundAlpha = 0.7;
	const borderAlpha = 1.0;

	// Process datasets into legend items with memoization
	const legendItems = useMemo(() => {
		if (!datasets || datasets.length === 0) return [];

		const items = datasets.map((dataset, index) => {
			// Handle arrays (datasets with different colors per bar)
			let bgColor = dataset.backgroundColor;
			let borderColor = dataset.borderColor;

			if (Array.isArray(bgColor)) {
				if (customColorForArrays) {
					// Use custom color for arrays
					bgColor = tinycolor(customColorForArrays).setAlpha(backgroundAlpha).toRgbString();
					borderColor = tinycolor(customColorForArrays).setAlpha(borderAlpha).toRgbString();
				} else {
					// Use first item color (string graph)
					bgColor = bgColor[0];
					borderColor = borderColor[0];
				}
			}
			
			return {
				text: dataset.label,
				borderColor: borderColor,
				backgroundColor: bgColor,
				hidden: dataset.hidden || false,
				index: index,
			};
		});

		// Apply custom sorting if provided
		if (sortFunction) {
			items.sort((a, b) => sortFunction(a, b, (item) => item.text));
		}
		
		return items;
	}, [datasets, sortFunction, customColorForArrays]);

	return (
		<>
			{legendItems.map((item) => (
				<div
					key={item.index}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						opacity: item.hidden ? 0.5 : 1,
						cursor: 'default',
					}}
				>
					<div
						style={{
							width: '15px',
							height: '15px',
							backgroundColor: item.backgroundColor,
							border: `2px solid ${item.borderColor}`,
							borderRadius: '2px',
						}}
					/>
					<span style={{ 
						fontSize: '12px',
						fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
						color: '#666',
					}}>
						{item.text}
					</span>
				</div>
			))}
		</>
	);
}